import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, Print } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetOrderReturnQuery } from '../services/returnApi';
import ThermalReturnReceipt80mm from '../components/pos/receipt/ThermalReturnReceipt80mm';
import { mapReturnToReceipt } from '../components/pos/receipt/mapReturnToReceipt';
import { useGetReceiptTemplateQuery } from '../services/receiptTemplateApi';
import { usePrinterContext } from '../context/PrinterContext';

const PrintReturnPage = () => {
  const { orderId, returnId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isFetching, isError } = useGetOrderReturnQuery(
    { orderId, id: returnId },
    { skip: !orderId || !returnId },
  );
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const receipt = useMemo(() => mapReturnToReceipt(data, receiptTemplate), [data, receiptTemplate]);

  const searchParams = new URLSearchParams(location.search || '');
  const shouldAutoPrint =
    location.state?.autoPrint === true || searchParams.get('autoprint') === '1';

  const { isConnected, silentPrintReturn } = usePrinterContext();
  const printerRef = useRef(null);
  printerRef.current = { isConnected, silentPrintReturn };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = receipt ? `Return ${receipt.returnNumber}` : 'Print Return';
    return () => {
      document.title = previousTitle;
    };
  }, [receipt]);

  useEffect(() => {
    if (!receipt || !shouldAutoPrint) return;
    const timer = window.setTimeout(async () => {
      const { isConnected: connected, silentPrintReturn: silentPrint } = printerRef.current;
      if (connected) {
        try {
          await silentPrint(receipt);
          return;
        } catch {
          // companion error — fall through to browser print
        }
      }
      window.print();
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [receipt, shouldAutoPrint]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E2E8F0', p: { xs: 1.5, sm: 2.5 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          variant="contained"
          onClick={() => window.print()}
          disabled={!receipt}
          sx={{ textTransform: 'none' }}
        >
          Print Return
        </Button>
      </Stack>

      <Paper
        sx={{ maxWidth: 'fit-content', mx: 'auto', p: { xs: 1, sm: 2 }, borderRadius: '16px' }}
      >
        {isFetching ? (
          <Stack alignItems="center" spacing={1} sx={{ width: '80mm', py: 8 }}>
            <CircularProgress size={24} />
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              Loading return receipt...
            </Typography>
          </Stack>
        ) : isError || !receipt ? (
          <Stack alignItems="center" spacing={1} sx={{ width: '80mm', py: 8 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#DC2626', fontWeight: 700 }}>
              Unable to load return receipt.
            </Typography>
            <Typography
              sx={{ fontSize: '0.75rem', color: '#64748B', textAlign: 'center' }}
            >
              Go back and try printing again.
            </Typography>
          </Stack>
        ) : (
          <ThermalReturnReceipt80mm receipt={receipt} />
        )}
      </Paper>
    </Box>
  );
};

export default PrintReturnPage;
